/**
 * Codec for the pending post-auth redirect stash (sessionStorage).
 *
 * The stash keeps the `?redirect=` target alive across the login flow's
 * competing navigations AND across full page reloads (session-expiry bounce
 * to /login, the bootstrap-error reload button). Durability across reloads is
 * why entries carry a timestamp: a stash is only honored within
 * PENDING_REDIRECT_TTL_MS of capture, so a stale target can't ambush an
 * unrelated login days later from the same tab.
 *
 * Pure module (no DOM, no store imports) so `npx tsx` can run its tests.
 */

export type PendingRedirectStash = {
  url: string;
  /** Capture time (ms epoch). Negative = legacy pre-TTL entry, treated as fresh. */
  timestamp: number;
};

export const PENDING_REDIRECT_TTL_MS = 30 * 60 * 1000;

/**
 * Sentinel timestamp for entries written by the previous deploy, which stored
 * the bare URL string. Treated as never-expired: a user mid-login across the
 * deploy boundary keeps their bounce (the allowlist still gates safety; the
 * TTL only bounds staleness).
 */
const LEGACY_TIMESTAMP = -1;

export function serializePendingRedirect(url: string, now: number): string {
  const stash: PendingRedirectStash = { url, timestamp: now };
  return JSON.stringify(stash);
}

export function parsePendingRedirect(raw: string | null): PendingRedirectStash | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'url' in parsed &&
      typeof parsed.url === 'string' &&
      parsed.url !== '' &&
      'timestamp' in parsed &&
      typeof parsed.timestamp === 'number' &&
      Number.isFinite(parsed.timestamp)
    ) {
      return { url: parsed.url, timestamp: parsed.timestamp };
    }
    // Valid JSON but not our shape (e.g. a bare number). A JSON-parseable
    // legacy URL string is impossible ('https://…' isn't valid JSON), so
    // anything landing here is garbage.
    return null;
  } catch {
    // Legacy format: the previous deploy stored the URL/path as a bare string.
    const trimmed = raw.trim();
    if (trimmed.includes('://') || trimmed.startsWith('/')) {
      return { url: trimmed, timestamp: LEGACY_TIMESTAMP };
    }
    return null;
  }
}

export function isPendingRedirectExpired(stash: PendingRedirectStash, now: number): boolean {
  if (stash.timestamp < 0) {
    return false; // legacy entry — no capture time known
  }
  // Strictly greater: age exactly at the TTL is still valid. A future
  // timestamp (client clock skew) yields a negative age → not expired.
  return now - stash.timestamp > PENDING_REDIRECT_TTL_MS;
}
